import React, { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import CelebrantPicker from "@/components/fliers/CelebrantPicker";
import StudioCanvas from "@/components/fliers/StudioCanvas";
import StudioControls from "@/components/fliers/StudioControls";
import { fmtBirthday } from "@/components/fliers/flierConfig";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Eye, Wand2, Cake, Save, Image as ImageIcon, Type, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const LAYERS = [{ key: "photo", label: "Photo", icon: ImageIcon }, { key: "name", label: "Name", icon: Type }, { key: "date", label: "Date", icon: CalendarDays }];

// Flier Studio — place the celebrant's photo, name and date straight on the organisation's flier
export default function Studio({ templates, initialTemplate, onCreated, onNeedTemplate, onLayoutSaved }) {
  const [tid, setTid] = useState(initialTemplate || "");
  const [zones, setZones] = useState(null);
  const [tplUrl, setTplUrl] = useState(null);
  const [active, setActive] = useState("photo");
  const [member, setMember] = useState(null);
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [file, setFile] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [post, setPost] = useState(true);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const tpl = useMemo(() => templates.find((t) => t.id === tid), [templates, tid]);

  useEffect(() => { if (initialTemplate) setTid(initialTemplate); }, [initialTemplate]);
  useEffect(() => { if (!tid && templates[0]) setTid(templates[0].id); }, [templates, tid]);
  useEffect(() => {
    if (!tpl) return;
    setZones(JSON.parse(JSON.stringify(tpl.zones)));
    let u; api.get(`/fliers/templates/${tpl.id}/image`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setTplUrl(u); }).catch(() => toast.error("Could not load the template"));
    return () => u && URL.revokeObjectURL(u);
  }, [tpl]);
  useEffect(() => {
    let u;
    if (file) { u = URL.createObjectURL(file); setPhotoUrl(u); }
    else if (member?.photo_path) api.get(`/image/${member.photo_path}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setPhotoUrl(u); }).catch(() => setPhotoUrl(null));
    else setPhotoUrl(null);
    return () => u && URL.revokeObjectURL(u);
  }, [file, member]);

  const pick = (m) => { setMember(m); setName(m.name); setDate(fmtBirthday(m.birthday)); };
  const form = () => { const d = new FormData(); d.append("template_id", tid); d.append("name", name.trim()); d.append("date_text", date.trim()); d.append("member_id", member?.id || ""); d.append("zones", JSON.stringify(zones)); if (file) d.append("file", file); return d; };
  const doPreview = async () => {
    setBusy("preview");
    try { const r = await api.post("/fliers/preview", form(), { responseType: "blob" }); setPreview(URL.createObjectURL(r.data)); }
    catch { toast.error("Preview failed — check the template and name"); } finally { setBusy(""); }
  };
  const saveLayout = async () => {
    setBusy("layout");
    try { await api.put(`/fliers/templates/${tid}/zones`, { zones }); toast.success("Layout saved — Autopilot will use it too"); onLayoutSaved?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const create = async () => {
    setBusy("create");
    try {
      const d = form(); d.append("save_layout", "true"); d.append("post_to_mess", String(post)); d.append("caption", caption.trim());
      await api.post("/fliers/compose", d);
      toast.success(post ? "Flier created and posted to the Galleon Mess 🎂" : "Flier created"); onLayoutSaved?.(); onCreated();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!templates.length) {
    return <Card className="gold-ring glass p-8 text-center" data-testid="compose-no-template"><Cake className="mx-auto h-8 w-8 text-[hsl(var(--gold))]" /><p className="mt-3 text-sm text-muted-foreground">Upload the organisation's birthday flier first.</p><Button onClick={onNeedTemplate} className="mt-4 bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" data-testid="compose-go-templates">Go to Templates</Button></Card>;
  }
  const ready = tid && name.trim() && zones;
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-5" data-testid="flier-studio" data-no-auto-refresh="true">
      <div className="space-y-3 lg:col-span-3">
        {templates.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid="studio-templates">
            {templates.map((t) => <button key={t.id} type="button" onClick={() => setTid(t.id)} className={`overflow-hidden rounded-lg border-2 transition-colors ${tid === t.id ? "border-[hsl(var(--gold))]" : "border-transparent hover:border-[hsl(var(--gold))]/50"}`} data-testid={`compose-template-${t.id}`}><div className="h-14 w-14 bg-black/20"><AuthImage src={`/fliers/templates/${t.id}/image`} className="h-full w-full object-cover" /></div></button>)}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {LAYERS.map(({ key, label, icon: Icon }) => <button key={key} type="button" onClick={() => setActive(key)} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-700 transition-colors ${active === key ? "bg-[hsl(var(--gold))] text-[#0A192F]" : "bg-secondary hover:bg-secondary/70"}`} data-testid={`studio-pick-${key}`}><Icon className="h-3.5 w-3.5" />{label}</button>)}
          <span className="ml-auto text-[0.65rem] text-muted-foreground">Drag a layer to move it · pull the gold dot to resize</span>
        </div>
        <div className="mx-auto max-w-[560px]">{zones && <StudioCanvas templateUrl={tplUrl} zones={zones} setZones={setZones} photoUrl={photoUrl} name={name} date={date} active={active} setActive={setActive} />}</div>
      </div>

      <Card className="gold-ring glass h-fit space-y-4 p-5 lg:col-span-2" data-testid="compose-card">
        <CelebrantPicker member={member} onPick={pick} onClear={() => setMember(null)} file={file} onFile={setFile} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label className="text-xs">Name on flier</Label><Input value={name} onChange={(e) => setName(e.target.value)} onFocus={() => setActive("name")} placeholder="e.g. Buka" className="mt-1" data-testid="compose-name-input" /></div>
          <div><Label className="text-xs">Date on flier</Label><Input value={date} onChange={(e) => setDate(e.target.value)} onFocus={() => setActive("date")} placeholder="e.g. 2nd September" className="mt-1" data-testid="compose-date-input" /></div>
        </div>
        {zones && <StudioControls active={active} zone={zones[active]} onChange={(z) => setZones((s) => ({ ...s, [active]: z }))} />}
        <div className="rounded-lg border border-[hsl(var(--water))]/30 bg-[hsl(var(--water))]/5 p-3">
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={post} onCheckedChange={(v) => setPost(!!v)} data-testid="compose-post-checkbox" />Post to the Galleon Mess as a birthday shout-out</label>
          {post && <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={`🎂 Happy Birthday ${name || "Brother"}! ${date}`.trim()} className="mt-2" data-testid="compose-caption-input" />}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gold-ring" onClick={doPreview} disabled={!ready || !!busy} data-testid="compose-preview-button">{busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Eye className="mr-1.5 h-4 w-4" />Exact preview</>}</Button>
          <Button variant="outline" className="gold-ring" onClick={saveLayout} disabled={!zones || !!busy} data-testid="studio-save-layout-button">{busy === "layout" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" />Save layout</>}</Button>
          <Button className="bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" onClick={create} disabled={!ready || !!busy} data-testid="compose-create-button">{busy === "create" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="mr-1.5 h-4 w-4" />Create flier</>}</Button>
        </div>
      </Card>

      {preview && (
        <Dialog open onOpenChange={() => setPreview(null)}>
          <DialogContent className="glass max-w-lg" data-testid="compose-preview-card">
            <DialogHeader><DialogTitle className="font-display">Exact preview — {name}</DialogTitle></DialogHeader>
            <img src={preview} alt="Flier preview" className="max-h-[75vh] w-full rounded-lg object-contain" data-testid="compose-preview-image" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
